import { civilFromDays, daysFromCivil } from './civil.ts'

export class LocalDateFormatError extends Error {
  readonly code = 'domain.local_date_format'
  readonly value: string
  constructor(value: string) {
    super(`"${value}" is not an ISO-8601 calendar date`)
    this.name = 'LocalDateFormatError'
    this.value = value
  }
}

declare const LocalDateBrand: unique symbol

/** A calendar day with no timezone, held as days since 1970-01-01. */
export interface LocalDate {
  readonly epochDay: number
  readonly [LocalDateBrand]: true
}

const ISO_DATE = /^(-?\d{4,})-(\d{2})-(\d{2})$/

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const

export function lengthOfMonth(year: number, month: number): number {
  return month === 2 && isLeapYear(year) ? 29 : MONTH_LENGTHS[month - 1]!
}

export function localDate(year: number, month: number, day: number): LocalDate {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new LocalDateFormatError(`${year}-${month}-${day}`)
  }
  if (month < 1 || month > 12 || day < 1 || day > lengthOfMonth(year, month)) {
    throw new LocalDateFormatError(`${year}-${month}-${day}`)
  }
  return { epochDay: daysFromCivil(year, month, day) } as LocalDate
}

export function parseLocalDate(value: string): LocalDate {
  const match = ISO_DATE.exec(value)
  if (!match) throw new LocalDateFormatError(value)
  const [, year, month, day] = match
  return localDate(Number(year), Number(month), Number(day))
}

export function localDateParts(value: LocalDate): { year: number; month: number; day: number } {
  return civilFromDays(value.epochDay)
}

export function formatLocalDate(value: LocalDate): string {
  const { year, month, day } = civilFromDays(value.epochDay)
  const sign = year < 0 ? '-' : ''
  const y = String(Math.abs(year)).padStart(4, '0')
  return `${sign}${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function plusDays(value: LocalDate, days: number): LocalDate {
  return { epochDay: value.epochDay + days } as LocalDate
}

export function plusMonths(value: LocalDate, months: number): LocalDate {
  const { year, month, day } = civilFromDays(value.epochDay)
  const total = year * 12 + (month - 1) + months
  const targetYear = Math.floor(total / 12)
  const targetMonth = total - targetYear * 12 + 1
  return localDate(targetYear, targetMonth, Math.min(day, lengthOfMonth(targetYear, targetMonth)))
}

/** Whole months, except that a month-end date lands on the target month's last day, so a
 * sequence of steps never drifts a month-end anchor through a shorter month. */
export function plusMonthsKeepingMonthEnd(value: LocalDate, months: number): LocalDate {
  const target = plusMonths(value, months)
  return endOfMonth(value).epochDay === value.epochDay ? endOfMonth(target) : target
}

export function startOfMonth(value: LocalDate): LocalDate {
  const { year, month } = civilFromDays(value.epochDay)
  return localDate(year, month, 1)
}

export function endOfMonth(value: LocalDate): LocalDate {
  const { year, month } = civilFromDays(value.epochDay)
  return localDate(year, month, lengthOfMonth(year, month))
}

/** ISO-8601 day of week: 1 = Monday .. 7 = Sunday. */
export function dayOfWeek(value: LocalDate): number {
  return ((((value.epochDay + 3) % 7) + 7) % 7) + 1
}

export function startOfWeek(value: LocalDate): LocalDate {
  return plusDays(value, 1 - dayOfWeek(value))
}

export function plusWeeks(value: LocalDate, weeks: number): LocalDate {
  return plusDays(value, weeks * 7)
}

export function startOfYear(value: LocalDate): LocalDate {
  const { year } = civilFromDays(value.epochDay)
  return localDate(year, 1, 1)
}

export function plusYears(value: LocalDate, years: number): LocalDate {
  const { year, month, day } = civilFromDays(value.epochDay)
  const targetYear = year + years
  return localDate(targetYear, month, Math.min(day, lengthOfMonth(targetYear, month)))
}

/** ISO-8601 week-year: the calendar year containing the Thursday of this date's week. */
export function isoWeekYear(value: LocalDate): number {
  const thursday = plusDays(value, 4 - dayOfWeek(value))
  return civilFromDays(thursday.epochDay).year
}

/** ISO-8601 week number (1-53) within `isoWeekYear`. */
export function isoWeekNumber(value: LocalDate): number {
  const thursday = plusDays(value, 4 - dayOfWeek(value))
  const yearStart = localDate(civilFromDays(thursday.epochDay).year, 1, 1)
  return Math.floor(daysBetween(yearStart, thursday) / 7) + 1
}

export function daysBetween(from: LocalDate, until: LocalDate): number {
  return until.epochDay - from.epochDay
}

export function compareLocalDate(a: LocalDate, b: LocalDate): -1 | 0 | 1 {
  return a.epochDay < b.epochDay ? -1 : a.epochDay > b.epochDay ? 1 : 0
}

export function equalsLocalDate(a: LocalDate, b: LocalDate): boolean {
  return a.epochDay === b.epochDay
}

export function minLocalDate(a: LocalDate, b: LocalDate): LocalDate {
  return a.epochDay <= b.epochDay ? a : b
}

export function maxLocalDate(a: LocalDate, b: LocalDate): LocalDate {
  return a.epochDay >= b.epochDay ? a : b
}
